import { Box, Pagination, Spinner } from '../../../blocks';
import { useQueryClient } from '@tanstack/react-query';
import { PointsVaultListColumns } from './PointsVaultListColumns';
import { PointsVaultListItem } from './PointsVaultListItem';
import {
  pointsVaultRejectedUsers,
  useGetPointsVaultApprovedUsersPaginated,
  usePointsVaultToken,
} from '../../../queries';
import { useEffect, useState } from 'react';
import { LeaderBoardNullState } from '../../Leaderboard/List/LeaderboardNullState';

type PointsVaultApprovedListProps = {
  query: {
    wallet?: string;
    twitter?: string;
  };
};

const PointsVaultApprovedList = ({ query }: PointsVaultApprovedListProps) => {
  const token = usePointsVaultToken();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data, isLoading, isError, refetch, isFetching } = useGetPointsVaultApprovedUsersPaginated({
    status: 'COMPLETED',
    token,
    pageSize,
    page: currentPage,
    twitter: query.twitter,
    wallet: query.wallet,
    activityTypeId: 'follow_push_on_twitter',
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [query.twitter, query.wallet]);

  const pointsVaultList = isLoading ? Array(5).fill(0) : data?.activities || [];
  const totalItems = data?.total || 0;

  if (!pointsVaultList.length && !isLoading) {
    return (
      <LeaderBoardNullState
        refetchLeaderboard={isError ? refetch : undefined}
        heading="No Users Found"
        error={isError}
        subHeading={isError ? 'Please refresh to view the List' : ''}
      />
    );
  }

  const handleRefetch = () => {
    refetch();
    queryClient.invalidateQueries({ queryKey: [pointsVaultRejectedUsers] });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <Box
      gap="spacing-sm"
      display="flex"
      flexDirection="column"
    >
      <Box
        height="calc(100vh - 420px)"
        overflow="auto"
      >
        <PointsVaultListColumns />
        {pointsVaultList.map((item, index) => (
          <PointsVaultListItem
            key={item?.activityId || index}
            item={item}
            isLoading={isLoading}
            refetch={handleRefetch}
          />
        ))}
        {isFetching && !isLoading && (
          <Box
            margin="spacing-xs"
            display="flex"
            justifyContent="center"
          >
            <Spinner size="medium" variant="primary" />
          </Box>
        )}
      </Box>
      {totalItems > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          padding="spacing-sm spacing-none"
        >
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={totalItems}
            onChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
};

export { PointsVaultApprovedList };
